
import { ScanditRefs } from './cleanupUtils';
import { waitForVideoContainer } from './scannerInit';
import { createBarcodeListener } from './barcodeHandler';

export const startScanditScanning = async (
  refs: ScanditRefs,
  videoRef: React.RefObject<HTMLDivElement>,
  isInitialized: boolean,
  handleBarcodeDetected: (barcode: string, confidence?: number) => Promise<void>,
  setDetectionStrength: (value: number | ((prev: number) => number)) => void,
  setError: (error: string | null) => void,
  setIsScanning: (scanning: boolean) => void,
  hasDetectedBarcodeRef: React.MutableRefObject<boolean>,
  toast: any
) => {
  if (!isInitialized || !refs.contextRef.current || !refs.ScanditCoreRef.current || !refs.ScanditBarcodeRef.current) {
    setError('Scanner not properly initialized');
    return;
  }

  try {
    console.log('🚀 Starting Scandit barcode scanner...');
    setError(null);
    setDetectionStrength(0);
    setIsScanning(true);
    hasDetectedBarcodeRef.current = false;
    
    // Wait for video container to be available
    await waitForVideoContainer(videoRef);

    const ScanditCore = refs.ScanditCoreRef.current;
    const ScanditBarcode = refs.ScanditBarcodeRef.current;

    // Create fresh view
    const view = await ScanditCore.DataCaptureView.forContext(refs.contextRef.current);
    refs.viewRef.current = view;
    view.connectToElement(videoRef.current);

    // Configure barcode capture
    const settings = new ScanditBarcode.BarcodeCaptureSettings();
    const symbologies = [
      ScanditBarcode.Symbology.Code128,
      ScanditBarcode.Symbology.Code39,
      ScanditBarcode.Symbology.EAN8,
      ScanditBarcode.Symbology.EAN13UPCA,
      ScanditBarcode.Symbology.UPCE,
      ScanditBarcode.Symbology.QR,
      ScanditBarcode.Symbology.DataMatrix,
      ScanditBarcode.Symbology.PDF417
    ];
    
    settings.enableSymbologies(symbologies);
    
    const barcodeCapture = await ScanditBarcode.BarcodeCapture.forContext(refs.contextRef.current, settings);
    refs.barcodeCaptureRef.current = barcodeCapture;
    
    // Create listener
    const listener = createBarcodeListener(handleBarcodeDetected, setDetectionStrength, setError);
    
    refs.listenerRef.current = listener;
    barcodeCapture.addListener(listener);
    
    // Create overlay with error handling
    try {
      const overlay = await ScanditBarcode.BarcodeCaptureOverlay.withBarcodeCaptureForView(
        barcodeCapture, 
        view
      );
      
      const viewfinder = new ScanditCore.RectangularViewfinder(
        ScanditCore.RectangularViewfinderStyle.Square,
        ScanditCore.RectangularViewfinderLineStyle.Light
      );
      overlay.setViewfinder(viewfinder);
    } catch (overlayError) {
      console.warn('⚠️ Could not create overlay:', overlayError);
    }
    
    // Enable and start
    barcodeCapture.setEnabled(true);
    await refs.cameraRef.current.switchToDesiredState('on');
    
    toast({
      title: "📹 Scandit Scanner Started",
      description: "Point your camera at a barcode",
      duration: 2000,
    });

  } catch (error: any) {
    console.error('❌ Scanner start error:', error);
    setError(`Failed to start scanner: ${error.message}`);
    setIsScanning(false);
  }
};

export const stopScanditScanning = async (
  refs: ScanditRefs,
  videoRef: React.RefObject<HTMLDivElement>,
  setIsScanning: (scanning: boolean) => void,
  setDetectionStrength: (strength: number) => void
) => {
  if (refs.isCleaningUpRef.current) {
    console.log('⚠️ Stop scanning called but cleanup already in progress');
    return;
  }
  
  console.log('🛑 Stopping Scandit scanner...');
  setIsScanning(false);
  setDetectionStrength(0);
  
  try {
    // Disable barcode capture first
    if (refs.barcodeCaptureRef.current) {
      try {
        refs.barcodeCaptureRef.current.setEnabled(false);
        if (refs.listenerRef.current) {
          refs.barcodeCaptureRef.current.removeListener(refs.listenerRef.current);
          refs.listenerRef.current = null;
        }
      } catch (e) {
        console.warn('Error disabling barcode capture:', e);
      }
      refs.barcodeCaptureRef.current = null;
    }
    
    // Turn off camera
    if (refs.cameraRef.current) {
      try {
        await refs.cameraRef.current.switchToDesiredState('off');
        console.log('✅ Camera turned off in stopScanning');
      } catch (e) {
        console.warn('Error turning off camera in stopScanning:', e);
      }
    }
    
    // Clean up view
    if (refs.viewRef.current) {
      try {
        if (videoRef.current) {
          refs.viewRef.current.connectToElement(null);
        }
        
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (typeof refs.viewRef.current.dispose === 'function') {
          await refs.viewRef.current.dispose();
        }
      } catch (viewError) {
        console.warn('⚠️ View cleanup warning in stopScanning:', viewError);
      }
      refs.viewRef.current = null;
    }
    
    // Clear video container
    if (videoRef.current) {
      videoRef.current.innerHTML = '';
    }
    
    console.log('✅ Scanner stopped successfully');
  } catch (e) {
    console.error('❌ Error stopping scanner:', e);
  }
};
